//CRN_20251218 - ⚠️ ngOnInit() called for components/directives & not services ⚠️

import { AlertDefaultTemplateIdDescriptionDTO } from 'src/app/core/models/system/alert/alert-default-template-id-description.dto';
import { AlertDTO } from 'src/app/core/models/system/alert/alert.dto';
import { AlertDeviceDTO } from 'src/app/core/models/system/alert/alertDevice.dto';
import { AlertGroupDTO } from 'src/app/core/models/system/alert/alertGroup.dto';
import { AlertSummaryDTO } from 'src/app/core/models/system/alert/alert-summary-dto';
import { AlertTypeIdDescriptionDTO } from 'src/app/core/models/system/alert/alert-type-id-description.dto';
import { EncryptionService } from '../../general/encryption-service';
import { environment } from 'src/environments/environment';
import { eSimApiPaths } from 'src/app/core/consts';
import { ExceptionsService } from '../../general/exceptions-service';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { HttpClientService } from '../../general/http-client-service';
import { Injectable } from '@angular/core';
import { StringResultDTO } from 'src/app/core/interfaces/general/string-result';
import { ThrottleSpeedTypeIdDescriptionDTO } from 'src/app/core/models/system/alert/throttle-speed-type-id-description.dto';

@Injectable({ providedIn: 'root' })

export class AlertsService {
  
  constructor(
    private encryptionService: EncryptionService,
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { 
  }

  public get(): Observable<AlertDTO[]> {
    try {
      return this.httpClientService.get<AlertDTO[]>(environment.eSimApi + eSimApiPaths.Alerts);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getAlertDevices(alertId : number, allSims : boolean, allGroups : boolean, companyId : number, modifiedAlertGroups : AlertGroupDTO[], modifiedAlertSims : AlertDeviceDTO[]): Observable<AlertDeviceDTO[]> {
    try {
      return forkJoin({
                  alertIdEncResult            : this.encryptionService.encrypt_AesServerSideForWeb((alertId ?? 0).toString()),
                  companyIdEncResult          : this.encryptionService.encrypt_AesServerSideForWeb((companyId ?? -1).toString(), true),
                  modifiedAlertGroupsEncResult: this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(modifiedAlertGroups ?? [])),
                  modifiedAlertSimsEncResult  : this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(modifiedAlertSims ?? [])),
                })
                   .pipe(
                     switchMap(({ alertIdEncResult, companyIdEncResult, modifiedAlertGroupsEncResult, modifiedAlertSimsEncResult }) => {
                       const alertIdEnc            =alertIdEncResult.result;
                       const companyIdEncSS        =companyIdEncResult.result;
                       const modifiedAlertGroupsEnc=modifiedAlertGroupsEncResult.result;
                       const modifiedAlertSimsEnc  =modifiedAlertSimsEncResult.result;
                       
                       return this.httpClientService.post(environment.eSimApi+
                                                          eSimApiPaths.Alerts+"/"+
                                                          eSimApiPaths.AlertsGetDevices,
                                                          {
                                                            alertIdEnc                   : alertIdEnc,
                                                            allSims                      : allSims,
                                                            allGroups                    : allGroups,
                                                            companyIdEncSS               : companyIdEncSS,
                                                            modifiedAlertGroupAllocations: modifiedAlertGroupsEnc,
                                                            modifiedAlertSimAllocations  : modifiedAlertSimsEnc
                                                          },
                                                          undefined
                                                         );
                     })
                   );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
  
  public getById(id: number): Observable<AlertDTO> {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const alertIdEncSS=encResult.result;
                            
                            return this.httpClientService.getById(environment.eSimApi +
                                                                  eSimApiPaths.Alerts + "/" +
                                                                  eSimApiPaths.AlertsGetById +
                                                                  "?alertIdEncSS=" + alertIdEncSS, undefined); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getAlertDefaultTemplateIdAndDescription(alertGroupId: number) {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertGroupId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const alertGroupIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<AlertDefaultTemplateIdDescriptionDTO[]>(environment.eSimApi +
                                                                                                      eSimApiPaths.Alerts + "/" +
                                                                                                      eSimApiPaths.AlertDefaultTemplates + 
                                                                                                      "?alertGroupIdEncSS=" + alertGroupIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.alertDefaultTemplateIdAndDescriptionConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTypeAlertIdAndDescription(alertGroupId: number) {
    try {
      return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertGroupId, true)
             .pipe(
                switchMap((encResult: StringResultDTO) => {
                            const alertGroupIdEncSS=encResult.result;
                            
                            return this.httpClientService.get<AlertTypeIdDescriptionDTO[]>(environment.eSimApi +
                                                                                           eSimApiPaths.TypesAlert +
                                                                                           "?alertGroupIdEncSS=" + alertGroupIdEncSS)
                                   .pipe(
                                     map(x => x.map(this.alertTypeIdAndDescriptionConverter))
                                   ); 
                         })
                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getTypeThrottleSpeedIdAndDescription() {
    try {
      return this.httpClientService.get<ThrottleSpeedTypeIdDescriptionDTO[]>(environment.eSimApi +
                                                                             eSimApiPaths.TypesThrottleSpeed)
      .pipe(
        map(x => x.map(this.throttleSpeedTypeIdAndDescriptionConverter))
      );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  private alertDefaultTemplateIdAndDescriptionConverter(alertTemplate: AlertDefaultTemplateIdDescriptionDTO) {
    return {id: alertTemplate.id, description: alertTemplate.templateName};
  }

  private alertTypeIdAndDescriptionConverter(alertType: AlertTypeIdDescriptionDTO) {
    return {id: alertType.id, description: alertType.alertType};
  }

  private throttleSpeedTypeIdAndDescriptionConverter(throttleSpeedType: ThrottleSpeedTypeIdDescriptionDTO) {
    return {id: throttleSpeedType.id, description: throttleSpeedType.throttleSpeedType};
  }

  // public deleteAlertById(id: number): Observable<AlertDTO> {
  //   try {
  //     //CRN_20260123 - ⚠️ If reinstated ServerSideEncryption must be implemented ⚠️
  
  //     return this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>id)
  //            .pipe(
  //               switchMap((encResult: StringResultDTO) => {
  //                           const alertIdEnc=encResult.result;
                            
  //                           return this.httpClientService.delete(environment.eSimApi +
  //                                                                eSimApiPaths.Alerts + "/" +
  //                                                                eSimApiPaths.Alerts +
  //                                                                '?alertIdEnc=' + alertIdEnc); 
  //                        })
  //                 );
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return of();
  //   }
  // }

  public createNewAlertByTemplateId(alertTempateId: number, companyId: number): Observable<AlertDTO> {
    try {
      return forkJoin({
               alerttemplateIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertTempateId),
               companyIdEncResult      : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ alerttemplateIdEncResult, companyIdEncResult }) => {
                 const alerttemplateIdEnc=alerttemplateIdEncResult.result;
                 const companyIdEncSS    =companyIdEncResult.result;
                 
                 return this.httpClientService.post(environment.eSimApi +
                                                    eSimApiPaths.Alerts + '/' +
                                                    eSimApiPaths.AlertCreateNewAlertByTemplateId +
                                                    "?alerttemplateIdEnc=" + alerttemplateIdEnc +
                                                    "&companyIdEncSS=" + companyIdEncSS,
                                                    {}
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public deleteAlert(alertId : number, companyId : number): Observable<Boolean> {
    try {
      return forkJoin({
               alertIdEncResult  : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertId),
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ alertIdEncResult, companyIdEncResult }) => {
                 const alertIdEnc    =alertIdEncResult.result;
                 const companyIdEncSS=companyIdEncResult.result;
                 
                 return this.httpClientService.delete(environment.eSimApi +
                                                      eSimApiPaths.Alerts + '/' + eSimApiPaths.AlertsDelete +
                                                      "?alertIdEnc=" + alertIdEnc +
                                                      "&companyIdEncSS=" + companyIdEncSS
                                                     );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public optOutAlert(alertId : number, companyId : number, deviceId : number, userId : number): Observable<Boolean> {
    try {
      return forkJoin({
               alertIdEncResult  : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertId),
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               deviceIdEncResult : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>deviceId),
               userIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>userId)
             })
             .pipe(
               switchMap(({ alertIdEncResult, companyIdEncResult, deviceIdEncResult, userIdEncResult }) => {
                 const alertIdEnc    =alertIdEncResult.result;
                 const companyIdEncSS=companyIdEncResult.result;
                 const deviceIdEnc   =deviceIdEncResult.result;
                 const userIdEnc     =userIdEncResult.result;
                 
                 return this.httpClientService.post(
                          environment.eSimApi +
                          eSimApiPaths.Alerts + '/' +
                          eSimApiPaths.AlertsOptOut +
                          "?alertIdEnc="     + alertIdEnc +
                          "&companyIdEncSS=" + companyIdEncSS +
                          "&deviceIdEnc="    + deviceIdEnc +
                          "&userIdEnc="      + userIdEnc,
                          {}
                        );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public toggleAlert(alertId : number, companyId : number): Observable<Boolean> {
    try {
      return forkJoin({
               alertIdEncResult  : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertId),
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ alertIdEncResult, companyIdEncResult }) => {
                 const alertIdEnc    =alertIdEncResult.result;
                 const companyIdEncSS=companyIdEncResult.result;
                 
                 return this.httpClientService.post(environment.eSimApi + 
                                                    eSimApiPaths.Alerts + '/' +
                                                    eSimApiPaths.AlertsToggleActiveState +
                                                    "?alertIdEnc=" + alertIdEnc +
                                                    "&companyIdEncSS=" + companyIdEncSS,
                                                    {}
                                                   );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getCompanyAlerts(companyId: number, alertGroupId: number): Observable<AlertSummaryDTO[]> {
    try {
      return forkJoin({
               alertGroupIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alertGroupId),
               companyIdEncResult   : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true)
             })
             .pipe(
               switchMap(({ alertGroupIdEncResult, companyIdEncResult }) => {
                 const alertGroupIdEnc=alertGroupIdEncResult.result;
                 const companyIdEncSS =companyIdEncResult.result;
                 
                 return this.httpClientService.get<AlertSummaryDTO[]>(environment.eSimApi +
                                                                      eSimApiPaths.Alerts + '/' +
                                                                      eSimApiPaths.CompanyAlerts +
                                                                      "?companyIdEncSS=" + companyIdEncSS +
                                                                      "&alertGroupIdEnc=" + alertGroupIdEnc,
                                                                      undefined
                                                                     );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  public getDeviceAlerts(companyId:number, deviceId: number): Observable<AlertSummaryDTO[]> {
    try {
      return forkJoin({
               companyIdEncResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               deviceIdEncResult : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>deviceId)
             })
             .pipe(
               switchMap(({ companyIdEncResult, deviceIdEncResult }) => {
                 const companyIdEncSS=companyIdEncResult.result;
                 const deviceIdEnc   =deviceIdEncResult.result;
                 
                 return this.httpClientService.get<AlertSummaryDTO[]>(environment.eSimApi +
                                                                      eSimApiPaths.Alerts + '/' +
                                                                      eSimApiPaths.DeviceAlerts +
                                                                      "?companyIdEncSS=" + companyIdEncSS + 
                                                                      "&deviceIdEnc=" + deviceIdEnc,
                                                                      undefined
                                                                     );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }

  // public getAlertIdAndDescription(companyId : number): Observable<AlertDTO[]> {
  //   try {
  //     return this.httpClientService.get<AlertDTO[]>(environment.eSimApi + eSimApiPaths.Alerts + "/" + eSimApiPaths.AlertsIdAndDescription + '?companyId=' + companyId)
  //     .pipe(
  //       map(x => x.map(this.idAndDescriptionConverter))
  //     );
  //   }
  //   catch(e) {
  //     this.exceptionsService.handleException(<Error>e);
  //     return of();
  //   }
  // }

  public post(alert: AlertDTO): Observable<AlertDTO> {
    try {
      var companyId = alert.companyID;
      alert.companyID = 0;
      
      return forkJoin({
                       companyIdEncSSResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
                       objectEncResult     : this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(alert), false)
                     })
             .pipe(
                switchMap(({ companyIdEncSSResult, objectEncResult }) => {
                  const companyIdEncSS=companyIdEncSSResult.result;
                  const objectEncSS   =objectEncResult.result;
                            
                  return this.httpClientService.post(environment.eSimApi +
                                                     eSimApiPaths.Alerts + '/' +
                                                     eSimApiPaths.AlertsPost +
                                                     '?companyIdEncSS=' + companyIdEncSS,
                                                     { data: objectEncSS }
                                                    );
                })
              );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
    
  public put(alert: AlertDTO): Observable<AlertDTO> {
    try {
      if (alert.alertTypeId != 4 && alert.alertTypeId != 5) {
        alert.alertCountries = [];
      }
      var companyId = alert.companyID;
      alert.companyID = 0;

      return forkJoin({
               alertIdEncResult    : this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>alert.id),
               companyIdEncSSResult: this.encryptionService.encrypt_AesServerSideForWeb(<string><unknown>companyId, true),
               objectEncResult     : this.encryptionService.encrypt_AesServerSideForWeb(JSON.stringify(alert))
             })
             .pipe(
               switchMap(({ alertIdEncResult, companyIdEncSSResult, objectEncResult }) => {
                 const alertIdEnc    =alertIdEncResult.result;
                 const companyIdEncSS=companyIdEncSSResult.result;
                 const objectEncSS   =objectEncResult.result;
                 
                 return this.httpClientService.put(environment.eSimApi +
                                                   eSimApiPaths.Alerts + '/' +
                                                   eSimApiPaths.AlertsPut +
                                                   '?companyIdEncSS=' + companyIdEncSS,
                                                   undefined,
                                                   { data: objectEncSS, alertIdEnc: alertIdEnc },
                                                   undefined
                                                  );
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
      
  public save(alert: AlertDTO): Observable<any> {
    try {
      //JSON.stringify(modal);
      if (alert.id && <number>alert.id>0)
        return this.put(alert);
  
      return this.post(alert);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of();
    }
  }
}
